import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Loader2, Tag } from 'lucide-react';
import {
  fetchAdminSupportTickets,
  type AdminSupportTicketListItem,
  type AdminSupportTicketStatus,
} from '../../../lib/adminTools';

type SupportTicketsSectionProps = {
  theme: 'light' | 'dark';
  identity?: { uid?: string | null; email?: string | null } | null;
  onNavigate?: (page: any) => void;
};

const STATUS_ROWS: Array<{ key: 'ALL' | AdminSupportTicketStatus; label: string }> = [
  { key: 'ALL', label: 'All' },
  { key: 'OPEN', label: 'Open' },
  { key: 'IN_REVIEW', label: 'In review' },
  { key: 'RESOLVED', label: 'Resolved' },
  { key: 'CLOSED', label: 'Closed' },
];

const STATUS_CARD_STYLES: Record<AdminSupportTicketStatus | 'ALL', {
  color: string;
  hoverText: string;
  shadow: string;
  pulse?: boolean;
}> = {
  ALL: { color: 'bg-white', hoverText: 'group-hover:text-white', shadow: 'shadow-[0_0_8px_rgba(255,255,255,0.6)]' },
  OPEN: { color: 'bg-blue-500', hoverText: 'group-hover:text-blue-400', shadow: 'shadow-[0_0_8px_rgba(59,130,246,0.6)]' },
  IN_REVIEW: { color: 'bg-amber-500', hoverText: 'group-hover:text-amber-400', shadow: 'shadow-[0_0_8px_rgba(245,158,11,0.6)]', pulse: true },
  RESOLVED: { color: 'bg-emerald-500', hoverText: 'group-hover:text-emerald-400', shadow: 'shadow-[0_0_8px_rgba(16,185,129,0.6)]' },
  CLOSED: { color: 'bg-slate-500', hoverText: 'group-hover:text-slate-300', shadow: '' },
};

export function SupportTicketsSection({ theme, identity, onNavigate }: SupportTicketsSectionProps) {
  const isDark = theme === 'dark';
  const [tickets, setTickets] = useState<AdminSupportTicketListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!identity?.uid) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetchAdminSupportTickets({ uid: String(identity.uid), email: identity.email || null })
      .then((result) => {
        if (!cancelled) setTickets(Array.isArray(result.tickets) ? result.tickets : []);
      })
      .catch((reason) => {
        if (!cancelled) {
          setTickets([]);
          setError(String((reason as Error)?.message || 'Unable to load support tickets.'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [identity?.uid, identity?.email]);

  const counts = useMemo(() => {
    const next: Record<'ALL' | AdminSupportTicketStatus, number> = { ALL: tickets.length, OPEN: 0, IN_REVIEW: 0, RESOLVED: 0, CLOSED: 0 };
    tickets.forEach((ticket) => {
      if (ticket.status in next) next[ticket.status] += 1;
    });
    return next;
  }, [tickets]);

  const distribution = useMemo(() => STATUS_ROWS.filter(({ key }) => key !== 'ALL').map(({ key, label }) => ({
    key,
    label,
    value: counts[key],
    percentage: counts.ALL ? (counts[key] / counts.ALL) * 100 : 0,
  })), [counts]);

  const sectionClassName = isDark
    ? 'overflow-hidden rounded-2xl border border-zinc-800 bg-[#0F1117] shadow-2xl'
    : 'overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-[0_8px_20px_rgba(15,23,42,0.05)]';

  const headerClassName = isDark
    ? 'border-b-[0.8px] border-[rgba(30,41,59,0.7)] bg-gradient-to-r from-slate-800/70 to-slate-900/70 p-4 backdrop-blur-md'
    : 'border-b border-zinc-200 bg-zinc-50 p-4';
  const bodyClassName = isDark ? 'border-[0.8px] border-[rgba(30,41,59,0.7)] bg-[#0F1117] px-6 py-[22px] sm:px-8 sm:py-[22px]' : 'bg-white px-6 py-[22px] sm:px-8 sm:py-[22px]';
  const cardClassName = isDark
    ? 'relative overflow-hidden rounded-xl border border-slate-700 bg-slate-900/40 p-5 shadow-md transition-all duration-200 ease-in-out hover:-translate-y-0.5 hover:shadow-lg'
    : 'relative overflow-hidden rounded-xl border border-zinc-200 bg-zinc-50 p-5 shadow-sm transition-all duration-200 ease-in-out hover:-translate-y-0.5 hover:shadow-md';
  const mutedClassName = isDark ? 'text-slate-400' : 'text-zinc-500';
  const badgeClassName = isDark
    ? 'border-slate-700 bg-slate-900 text-slate-400'
    : 'border-zinc-200 bg-white text-zinc-500';

  return (
    <section className={sectionClassName} data-testid="admin-support-tickets">
      <div className={`${headerClassName} flex h-[84.8px] flex-col items-start justify-between gap-4 px-4 py-0 pt-[10px] sm:flex-row sm:items-center`}>
        <div>
          <div className="flex items-center gap-2">
            <Tag className="h-4 w-4 text-cyan-400" aria-hidden="true" />
            <h2 className={`text-xl font-semibold tracking-tight ${isDark ? 'text-white' : 'text-zinc-900'}`}>SUPPORT TICKETS</h2>
          </div>
          <p className={`mt-0 pb-[10px] text-sm font-normal ${mutedClassName}`}>Ticket status summary.</p>
        </div>
        {onNavigate ? (
          <button
            type="button"
            onClick={() => onNavigate('Support Tickets')}
            aria-label="Support Tickets"
            className={`inline-flex h-10 items-center justify-center rounded-lg border px-5 py-2.5 text-sm font-medium shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 ${isDark ? 'border-slate-700 bg-slate-800 text-white hover:border-slate-600 hover:bg-slate-700 focus:ring-offset-slate-900' : 'border-zinc-300 bg-white text-zinc-800 hover:border-zinc-400 hover:bg-zinc-50 focus:ring-offset-white'}`}
          >
            View All Tickets
            <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      {loading ? (
        <div className={`${bodyClassName} flex items-center gap-2 text-sm ${mutedClassName}`}>
          <Loader2 className="h-4 w-4 animate-spin text-blue-500" /> Loading ticket counts…
        </div>
      ) : error ? (
        <div className={`${bodyClassName} text-sm ${isDark ? 'text-rose-300' : 'text-rose-600'}`}>{error}</div>
      ) : (
        <div className={bodyClassName}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-5">
            {STATUS_ROWS.map(({ key, label }) => {
              const style = STATUS_CARD_STYLES[key];
              return (
                <div key={key} className={`${cardClassName} group`}>
                  <div className={`absolute left-0 top-0 h-1 w-full ${style.color} opacity-50 transition-opacity group-hover:opacity-100`} />
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div className="flex items-center space-x-2">
                      <span className={`h-2.5 w-2.5 rounded-full ${style.color} ${style.shadow} ${style.pulse ? 'animate-pulse' : ''}`} />
                      <span className={`text-xs font-semibold uppercase tracking-wider transition-colors ${mutedClassName} ${style.hoverText}`}>{label}</span>
                    </div>
                    {key !== 'ALL' ? (
                      <span className={`rounded border px-2 py-1 text-xs font-medium ${badgeClassName}`} title="Current ticket count">
                        {`${counts.ALL ? Math.round((counts[key] / counts.ALL) * 100) : 0}%`}
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className={`text-3xl font-bold tracking-tight sm:text-4xl ${isDark ? 'text-white' : 'text-zinc-900'}`}>{counts[key]}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className={`mt-3 border-t pt-[14px] ${isDark ? 'border-slate-700/50' : 'border-zinc-200'}`}>
            <div className="mb-2 flex items-center justify-between">
              <span className={`text-xs font-medium uppercase tracking-wider ${mutedClassName}`}>Ticket Distribution</span>
            </div>
            <div className={`flex h-2 w-full overflow-hidden rounded-full border ${isDark ? 'border-slate-700/50 bg-slate-800' : 'border-zinc-200 bg-zinc-100'}`}>
              {distribution.map((stat) => (
                <div
                  key={`dist-${stat.key}`}
                  className={`h-full ${STATUS_CARD_STYLES[stat.key].color}`}
                  style={{ width: `${stat.percentage}%` }}
                  title={`${stat.label}: ${stat.value}`}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default SupportTicketsSection;
